import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  type ElementRef,
  HostBinding,
  inject,
  Input,
  type OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { faColumns, faMinus, faPen, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { type ModalInstance, ModalWindowController } from '@reconcept-platform/shared/ui/core/modal';
import { ModalWindowCompareController } from '@reconcept-platform/shared/ui/core/modal/modal/modal-window/modal-window-compare.control';
import { ButtonIconOnlyComponent } from '@reconcept-platform/shared/ui/standalone/button';

@Component({
  selector: 'rcp-modal-window',
  styleUrls: ['./modal-window.component.scss'],
  template: `
    <div class="container" [ngClass]="ngClass">
      @if (isAdvanced) {
        <div class="sidebar-hidden-label"></div>
      }

      @if (showTitleBar || modalWindowController.displayMinimized()) {
        <!--  the new modals (legacy mode = false), we need to hide this element unless it is minimized -->
        <div
          class="modal-title no-print"
          (click)="clickHeader()"
          [class.comparing-header]="modalInstance.comparing"
          [class.hide]="!modalWindowController.displayMinimized()"
        >
          <span [title]="modalWindowController.title()">{{ modalWindowController.title() }}</span>

          <div class="buttons">
            @if (showCloseButton) {
              <rcp-button-icon-only (click)="clickCloseModal($event)" [icon]="faTimes" />
            }
          </div>
        </div>
      }

      <!-- inserted content -->
      <div class="content-container" #scrollableBody>
        <ng-container #dynamicModalContent></ng-container>
      </div>
    </div>

    @if (!modalWindowController.displayMinimized()) {
      <div class="overlay" [class.super-dark]="hideBackground" (click)="clickBackgroundOverlay()"></div>
    }
  `,
  providers: [ModalWindowController, ModalWindowCompareController],
  imports: [NgClass, ButtonIconOnlyComponent],
  host: {
    '[class.align-top]': "modalInstance.config.align === 'top'",
  },
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ModalWindowComponent implements OnInit {
  private modalWindowCompareController = inject(ModalWindowCompareController);

  modalWindowController = inject(ModalWindowController);

  @Input() modalInstance: ModalInstance;

  get showCloseButton(): boolean {
    // @ts-expect-error TS2322 type not assignable
    return this.modalInstance.config.showCloseButton;
  }

  get hideBackground(): boolean {
    // @ts-expect-error TS2322 type not assignable
    return this.modalInstance.config.hideBackground;
  }

  @HostBinding('class.scrollable')
  get isScrollable(): boolean {
    // @ts-expect-error TS2322 type not assignable
    return this.modalInstance?.config.scrollable && !this.isContentScrollable;
  }

  @HostBinding('class.content-scrollable')
  get isContentScrollable(): boolean {
    // @ts-expect-error TS2322 type not assignable
    return this.modalInstance?.config.contentScrollable;
  }

  @HostBinding('class.advanced')
  get isAdvanced(): boolean {
    return this.modalInstance && this.modalInstance.config.style === 'advanced';
  }

  @HostBinding('class.regular')
  get isRegular(): boolean {
    return this.modalInstance && this.modalInstance.config.style === 'regular';
  }

  @HostBinding('class.simple')
  get isSimple(): boolean {
    return this.modalInstance && this.modalInstance.config.style === 'simple';
  }

  @HostBinding('class.mini')
  get isMini(): boolean {
    return this.modalInstance && this.modalInstance.config.style === 'mini';
  }

  @HostBinding('class.as-sidebar')
  get displayAsSidebar(): boolean {
    // do not show modal as sidebar when comparing.
    if (this.modalWindowCompareController.isComparing()) {
      return false;
    }
    return this.modalWindowController.displayAsSidebar;
  }

  @HostBinding('class.minimized')
  get displayMinimized(): boolean {
    // do not show modal as sidebar when comparing.
    if (this.modalWindowCompareController.isComparing()) {
      return false;
    }

    return this.modalWindowController.displayMinimized();
  }

  @HostBinding('style')
  get moveIfMinimized(): any {
    if (!this.displayMinimized) {
      return {};
    }

    return {
      // right: modalIndex * (--width-xs + --item-spacing-between-md)
      right: this.modalWindowCompareController.modalIndex * (320 + 8) + 'px',
    };
  }

  @HostBinding('class.compare')
  get isComparing(): boolean {
    return this.modalWindowCompareController.isComparing();
  }

  @HostBinding('class.compare-left')
  get isLeft(): boolean {
    return this.modalWindowCompareController.isComparing() && this.modalWindowCompareController.isLeft();
  }

  @HostBinding('class.compare-right')
  get isRight(): boolean {
    return this.modalWindowCompareController.isComparing() && this.modalWindowCompareController.isRight();
  }

  @ViewChild('dynamicModalContent', { read: ViewContainerRef, static: true })
  dynamicModalContent: ViewContainerRef;

  @ViewChild('scrollableBody', { static: true })
  scrollableBody: ElementRef;

  protected readonly faPen = faPen;
  protected readonly faColumns = faColumns;
  protected readonly faMinus = faMinus;
  protected readonly faTimes = faTimes;

  showTitleBar: boolean;
  ngClass: any;

  ngOnInit() {
    this.loadComponent(this.modalInstance);

    // register the content container
    this.modalWindowController.init(this.modalInstance);
    this.modalWindowController.registerScrollableBody(this.scrollableBody);

    // @ts-expect-error TS2322 type not assignable
    this.showTitleBar = this.modalInstance && this.modalInstance.config.showTitleBar;
    this.ngClass = this.modalInstance.config.class;
  }

  // show the modal; may be called from outside
  showModal() {
    this.modalWindowController.showModalNormal();
  }

  clickBackgroundOverlay() {
    // default behaviour is allowed
    if (this.modalInstance.config.allowBackgroundClose === false) {
      return;
    }

    // every other action then compare, means we want to stop comparing
    this.modalWindowController.setComparingMode(false);
    this.modalWindowController.dismiss(null);
  }

  clickToggleDisplayAsSidebar($event: any) {
    // stop propagation to prevent background click
    $event.stopPropagation();

    this.modalWindowController.toggleDisplayAsSidebar();
  }

  clickToggleDisplayMinimized($event: any) {
    // stop propagation to prevent background click
    $event.stopPropagation();

    // every other action then compare, means we want to stop comparing
    this.modalWindowController.setComparingMode(false);

    this.modalWindowController.toggleDisplayMinimized();
  }

  clickToggleDisplayMinimizedToCompare($event: MouseEvent) {
    $event.stopPropagation();

    // only when the compare button is clicked, we go into compare mode.
    // this means that the next opened modal, will be the one to compare with.
    this.modalWindowController.setComparingMode(true);
    this.modalWindowController.toggleDisplayMinimized();
  }

  clickHeader() {
    // when the current window was minimized to compare, but clicked again:
    // leave comparing mode and open the modal.
    if (this.modalInstance.comparing) {
      this.modalWindowController.setComparingMode(false);
      this.modalWindowController.showModalNormal();
    }

    if (this.displayMinimized && this.modalWindowController.isComparingMode()) {
      this.modalWindowController.startCompare();
    } else {
      this.modalWindowController.showModalNormal();
    }
  }

  clickCloseModal($event: any) {
    // prevent background click
    $event.stopPropagation();

    // close this modal
    this.modalWindowController.dismiss();
  }

  private loadComponent(modalInstance: ModalInstance) {
    // make sure we have a valid data object
    if (!modalInstance.config.data) {
      modalInstance.config.data = {};
    }

    // We create the component using the factory and the injector
    const component = this.dynamicModalContent.createComponent(modalInstance.component, {
      ...(modalInstance.environmentInjector ? { environmentInjector: modalInstance.environmentInjector } : {}),
      ...(modalInstance.injector ? { injector: modalInstance.injector } : {}),
    });

    // Every data key is offered to the component as an input. They are not filtered up front, because
    // inputs a component exposes through `hostDirectives` are not reported by reflectComponentType while
    // setInput does reach them. A key that is no input at all is skipped instead of failing the modal.
    for (const [key, value] of Object.entries(this.modalInstance.config.data)) {
      try {
        component.setInput(key, value);
      } catch (error) {
        if (!isUnknownInputError(error)) {
          throw error;
        }
      }
    }
  }
}

/** Angular's runtime error code for setting something that is not an input of the component.
 * note, this is validated in the spec, so if it changes the
 * */
const UNKNOWN_INPUT_ERROR_CODE = 303;

/**
 * Whether the error says the key was not an input, rather than the input itself having failed. Angular only
 * throws this when configured to (TestBed's `errorOnUnknownProperties`) and logs it otherwise, so a data key
 * that is not an input can still show up in the console.
 */
function isUnknownInputError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'code' in error && error.code === UNKNOWN_INPUT_ERROR_CODE;
}
